import React, { useState } from 'react';
import { PlusIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { Product } from '../../data';
import { useContent } from '../../ContentContext';
import { Field, TextArea, Modal, PrimaryButton, GhostButton } from './ui';
interface Draft extends Product {}
export function ProductManager() {
  const {
    businesses,
    addProduct,
    updateProduct,
    deleteProduct
  } = useContent();
  const [activeSlug, setActiveSlug] = useState(businesses[0]?.slug ?? '');
  const [editing, setEditing] = useState<Draft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const business = businesses.find(b => b.slug === activeSlug) ?? businesses[0];
  if (!business) {
    return <div data-id="element-136" data-mp="2hbfujxiiuleg">
        <h1 className="font-display text-3xl font-medium text-gray-900" data-id="element-137" data-mp="2hd0lld5mnm7k">Produkter</h1>
        <p className="mt-4 text-sm text-gray-500" data-id="element-138" data-mp="2vb8n7ydlzbp2">Skapa en partner först för att kunna lägga upp produkter.</p>
      </div>;
  }
  const openNew = () => {
    setEditing({
      id: `p-${Date.now()}`,
      name: '',
      category: '',
      price: 0,
      description: '',
      image: '',
      businessSlug: business.slug,
      businessName: business.name
    });
    setIsNew(true);
  };
  const openEdit = (product: Product) => {
    setEditing({
      ...product
    });
    setIsNew(false);
  };
  const save = () => {
    if (!editing) return;
    const payload: Product = {
      ...editing,
      businessSlug: business.slug,
      businessName: business.name
    };
    if (isNew) addProduct(business.slug, payload);else updateProduct(business.slug, editing.id, payload);
    setEditing(null);
  };
  const set = <K extends keyof Draft,>(key: K, value: Draft[K]) => setEditing(prev => prev ? {
    ...prev,
    [key]: value
  } : prev);
  return <div data-id="element-139" data-mp="ywz87wy9evet">
      <div className="flex items-center justify-between" data-id="element-140" data-mp="13u7t45asldjr">
        <div data-id="element-141" data-mp="13vso1w10qyi5">
          <h1 className="font-display text-3xl font-medium text-gray-900" data-id="element-142" data-mp="13s4reo0a1vz7">Sortiment</h1>
          <p className="mt-2 text-sm text-gray-500" data-id="element-143" data-mp="18tkcuh6da4i4">Lägg till, ändra och ta bort produkter per partner.</p>
        </div>
        <PrimaryButton onClick={openNew} data-id="element-144" data-mp="j2u4tl9tlkj9">
          <span className="flex items-center gap-2" data-id="element-145" data-mp="e6vazi5q3abg">
            <PlusIcon className="h-4 w-4" data-id="element-146" data-mp="e4931hex6ssp" /> Ny produkt
          </span>
        </PrimaryButton>
      </div>

      {/* Partner switcher */}
      <div className="mt-8 flex flex-wrap gap-2" data-id="element-147" data-mp="o3qaa39tleqs">
        {businesses.map(b => <button key={b.slug} onClick={() => setActiveSlug(b.slug)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${b.slug === business.slug ? 'bg-black text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`} data-id="element-148" data-mp="11vek1zwljr4l">
            {b.name}
            <span className="ml-2 text-xs opacity-60" data-id="element-149" data-mp="16w2hp4fbz67s">{b.products.length}</span>
          </button>)}
      </div>

      {/* Live preview – exakt samma produktkort som i utbudet */}
      <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-id="element-150" data-mp="38n5wm5w976y5">
        {business.products.map(product => <div key={product.id} className="flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-premium" data-id="element-151" data-mp="1o4lmonhw35pp">
            <div className="relative h-40 overflow-hidden bg-gray-100" data-id="element-152" data-mp="1t599ewz350e4">
              {product.image && <img src={product.image} alt={product.name} className="h-full w-full object-cover" data-id="element-153" data-mp="1ee0eq9gbsou4" />}
              {product.category && <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-900 backdrop-blur-md" data-id="element-154" data-mp="15du20psgbsw0">
                  {product.category}
                </div>}
            </div>
            <div className="flex flex-1 flex-col p-6" data-id="element-155" data-mp="vh8xe5kcgjsp">
              <h3 className="font-display text-lg font-medium text-gray-900" data-id="element-156" data-mp="10dpmp33a547d">{product.name}</h3>
              <p className="mt-2 text-xs leading-relaxed text-gray-500 line-clamp-2" data-id="element-157" data-mp="2pt5uae1k2hv5">{product.description}</p>
              <div className="mt-auto flex items-center justify-between pt-5" data-id="element-158" data-mp="2urh3qnnxuqcu">
                <span className="text-base font-medium text-gray-900" data-id="element-159" data-mp="uqsp2rv56ast">{product.price} kr</span>
                <div className="flex gap-2" data-id="element-160" data-mp="prft9hmalte3">
                  <button onClick={() => openEdit(product)} aria-label={`Redigera ${product.name}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-900 transition-colors hover:bg-gray-200" data-id="element-161" data-mp="prpgixfydmg3">
                    <PencilIcon className="h-4 w-4" data-id="element-162" data-mp="bspuzynqlno5" />
                  </button>
                  <button onClick={() => {
                if (window.confirm(`Ta bort ${product.name}?`)) deleteProduct(business.slug, product.id);
              }} aria-label={`Ta bort ${product.name}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-163" data-mp="6zwqnbw6khcl">
                    <Trash2Icon className="h-4 w-4" data-id="element-164" data-mp="1zp61ue35x8o4" />
                  </button>
                </div>
              </div>
            </div>
          </div>)}

        <button onClick={openNew} className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-gray-200 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600" data-id="element-165" data-mp="24mpghf3rvngl">
          <PlusIcon className="h-7 w-7" data-id="element-166" data-mp="1gz7s7tjpv7id" />
          <span className="text-sm font-medium" data-id="element-167" data-mp="1h0iz428gkvdp">Lägg till produkt</span>
        </button>
      </div>

      {editing && <Modal title={isNew ? `Ny produkt – ${business.name}` : `Redigera ${editing.name}`} onClose={() => setEditing(null)} footer={<>
              <GhostButton onClick={() => setEditing(null)} data-id="element-169" data-mp="1us799zz5d9s1">Avbryt</GhostButton>
              <PrimaryButton onClick={save} data-id="element-170" data-mp="y7othywq6fm5">Spara</PrimaryButton>
            </>} data-id="element-168" data-mp="1lvepkxy1raop">
          <div className="grid gap-6 md:grid-cols-[1fr,260px]" data-id="element-171" data-mp="1288p2y1nhb66">
            <div className="grid gap-5" data-id="element-172" data-mp="25k5x52ntmrnk">
              <Field label="Namn" value={editing.name} onChange={v => set('name', v)} placeholder="Prinsesstårta" data-id="element-173" data-mp="25iuwhbpoa00o" />
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-174" data-mp="20ji82mj5kkvz">
                <Field label="Kategori" value={editing.category} onChange={v => set('category', v)} placeholder="Tårtor" hint="Skapar automatiskt ett filter i utbudet." data-id="element-175" data-mp="2f9h8kvy6k8em" />
                <Field label="Pris (kr)" type="number" value={String(editing.price)} onChange={v => set('price', Number(v) || 0)} data-id="element-176" data-mp="1hna5jqhiyo4d" />
              </div>
              <Field label="Bild (URL)" value={editing.image} onChange={v => set('image', v)} placeholder="https://..." data-id="element-177" data-mp="1rjmts8uaw9rq" />
              <TextArea label="Beskrivning" rows={3} value={editing.description ?? ''} onChange={v => set('description', v)} data-id="element-178" data-mp="373j9r9l1pawk" />
            </div>

            {/* Kortförhandsvisning */}
            <div data-id="element-179" data-mp="3fka5glabg7bh">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-180" data-mp="3fgm8td9kr4sj">Förhandsvisning</span>
              <div className="flex flex-col overflow-hidden rounded-3xl bg-white shadow-premium ring-1 ring-gray-100" data-id="element-181" data-mp="35kja6hdxloac">
                <div className="relative h-32 bg-gray-100" data-id="element-182" data-mp="3t9kcdmw68376">
                  {editing.image && <img src={editing.image} alt="" className="h-full w-full object-cover" data-id="element-183" data-mp="23swdgc3y6y3" />}
                  {editing.category && <div className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-gray-900" data-id="element-184" data-mp="3jj96vvwu9ly6">
                      {editing.category}
                    </div>}
                </div>
                <div className="p-4" data-id="element-185" data-mp="kuacmlfr7a2k">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-186" data-mp="kvbxtiqhhcy0">{business.name}</p>
                  <h4 className="mt-1 font-display text-base font-medium text-gray-900" data-id="element-187" data-mp="2gyzx1m9j518c">{editing.name || 'Produktnamn'}</h4>
                  <p className="mt-1 text-xs text-gray-500 line-clamp-2" data-id="element-188" data-mp="2c4lyjazkmdzg">{editing.description}</p>
                  <p className="mt-3 text-sm font-medium text-gray-900" data-id="element-189" data-mp="2830qq41w8pw4">{editing.price} kr</p>
                </div>
              </div>
            </div>
          </div>
        </Modal>}
    </div>;
}